import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import moment from "moment";
import { SelectModeData } from "PFApp/booking/booking_edit_mode_store";
import { ReassignSelectOption } from "PFApp/booking/components/reassign_booking_modal/bookings_to_reassign_selector.types";
import { useRowsChildrenInvalidate } from "PFApp/booking/store";
import { ProfileSource } from "PFApp/components/profile_source_toggle";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBookingInvalidate, useBookingUpdate } from "PFCore/hooks/queries";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useWorkforceInvalidate } from "PFCore/hooks/queries/bookings/workforce/use_workforce_invalidate";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { BulkOperationResponse } from "PFCore/services/bookings_bulk_operation";
import { Booking } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useBulkUpdate } from "../../info_overlay/bulk_operation_hooks/use_bulk_update";

const BULK_REASSIGN_BOOKINGS_SUCCESS_ACTION_ID = getGrowlId("bm-bulk-reassign-bookings");

type UseReassignSubmit = {
  onError?: VoidFunction;
  onSubmit?: VoidFunction;
  sourceProfileIds: number[];
  defaultTargetProfileId?: number;
  bookings: Booking[];
  bulkSelection?: SelectModeData | null;
  reassignTarget?: ProfileSource;
};

type UseReassignSubmitReturn = {
  handleSubmit: (reassignOption: ReassignSelectOption, targetProfileId?: number) => void;
};

export const useReassignSubmit = ({
  onError,
  onSubmit,
  sourceProfileIds,
  defaultTargetProfileId,
  bookings,
  bulkSelection,
  reassignTarget = ProfileSource.NamedResource
}: UseReassignSubmit): UseReassignSubmitReturn => {
  const { t } = useTranslation("bookingModule");
  const { showGrowl } = useGrowlStoreActions();
  const { getSingleErrorByType } = useResponseErrors();

  const onSuccess = ({ bookingsCount }: { bookingsCount: number }) => {
    showGrowl({
      id: BULK_REASSIGN_BOOKINGS_SUCCESS_ACTION_ID,
      kind: "success",
      message: t("bookings.reassign.sidePanel.reassignSuccess", { count: bookingsCount }),
      hidePreviousByTypeAndId: true,
      ttl: 5000
    });
  };
  const onBulkReassignError = () => {
    showGrowl({
      kind: "error",
      message: t("bookings.reassign.sidePanel.bulkReassignError")
    });
  };

  const { update: updateBooking } = useBookingUpdate({
    onSuccess: () => onSuccess({ bookingsCount: 1 }),
    onError
  });
  const { bulkUpdateDetails } = useBulkUpdate({
    onSuccess: (response: BulkOperationResponse) => onSuccess({ bookingsCount: response.bookingIds.length }),
    onError: onBulkReassignError
  });
  const { mutateAsync: createShortlist } = useShortlistCreate();

  const {
    invalidateByActivityIds: invalidateRowChildrenByActivityIds,
    invalidateByProfileIds: invalidateRowChildrenByProfileIds
  } = useRowsChildrenInvalidate();
  const { invalidate: invalidateWorkforce } = useWorkforceInvalidate();
  const { invalidate: invalidateBooking, invalidateProfileBookingsList } = useBookingInvalidate();
  const { invalidateActivityWarnings } = useActivityInvalidate();

  const { formatISODate } = useDateFormatter();

  const handleSubmit = async (reassignOption: ReassignSelectOption, targetProfileId?: number) => {
    const newProfileId = targetProfileId ?? defaultTargetProfileId;

    if (newProfileId === undefined || isEmpty(bookings)) {
      onSubmit?.();
      return;
    }
    // in case of reassigning a Match, it needs to be shortlisted first (to pass the named:false flag)
    if (reassignTarget === ProfileSource.Matches) {
      const activityId = compact(uniq(bookings.map(({ activity_id: activityId }) => activityId)));
      if (!isEmpty(activityId) && activityId.length === 1) {
        try {
          await createShortlist({ activityId: activityId[0], profileId: newProfileId, named: false });
        } catch (error) {
          const isShortlistUniquenessError = !!getSingleErrorByType(error)?.shortlist_uniqueness;
          if (!isShortlistUniquenessError) {
            throw error;
          } // else - continue, the shortlist already exists
        }
      }
    }
    if (bookings.length > 1) {
      bulkSelection && (await bulkUpdateDetails({ selection: bulkSelection, profileId: newProfileId }));
    } else {
      const booking = bookings[0];
      if (reassignOption === ReassignSelectOption.ThisBooking) {
        await updateBooking({ id: booking.id, profileId: newProfileId });
      } else {
        const min =
          reassignOption === ReassignSelectOption.ThisAndFuture
            ? formatISODate(booking.start_date)
            : formatISODate(moment().subtract(2, "year"));
        const max = formatISODate(moment().add(2, "year"));
        await bulkUpdateDetails({
          selection: {
            filters: [
              {
                activityId: booking.activity_id ?? undefined,
                profileId: booking?.profile_id
              }
            ],
            dateRange: {
              min,
              max
            }
          },
          profileId: newProfileId
        });
      }
    }
    invalidateWorkforce();
    [newProfileId, ...sourceProfileIds].forEach((profileId) => invalidateProfileBookingsList(profileId));
    invalidateRowChildrenByProfileIds([newProfileId]);
    bookings.forEach(({ id }) => invalidateBooking(id));
    const bookingActivityIds = uniq(compact(bookings.map((booking) => booking.activity_id)));
    if (!isEmpty(bookingActivityIds)) {
      invalidateRowChildrenByActivityIds(bookingActivityIds);
      bookingActivityIds.forEach((activityId) => invalidateActivityWarnings(activityId));
    }
    onSubmit?.();
  };

  return { handleSubmit };
};
